import {
  BindingOnChangeOptions,
  BindingOnCreateOptions,
  BindingOnDeleteOptions,
  BindingOnShapeChangeOptions,
  BindingUtil,
  Editor,
  IndexKey,
  TLBinding,
  TLShape,
  Vec,
} from "tldraw";

import { BindableUtil } from "@/components/canvas-editor/shapes/bindable";
import {
  isLogicShape,
  LogicShape,
  getMinWForLogic,
  LayoutBinding,
  CustomShapes,
  FeedShape,
  isCustomNodeShape,
  isFeedShape,
  isLayerShape,
  isAudienceLogicShape,
} from "@/components/canvas-editor/shapes/shapes";

/**
 * Inside the Feed, layers and rules are two stacks, not one.
 *
 * The Page section (Layer blocks) sits under the header, the Rules root below
 * it, with room between them for the "Rules" divider the Feed draws. Region
 * membership is decided by TYPE, not by a field on the binding: a Layer child
 * is Page, anything else is Rules. That keeps stored snapshots readable
 * without a migration and gives every reader one rule.
 */
const FEED_SECTION_GAP = 24;
/**
 * Layers sit INSIDE the Page box, which itself sits inside the Feed at the
 * same 16px inset the rules root has — so a layer is indented one more inset
 * than the rules root, and narrower by two.
 */
export const PAGE_REGION_INSET = 16;

export function isPageRegionChild(shape: { type: string }): boolean {
  return isLayerShape(shape as never);
}

export function isLayoutBinding(binding: TLBinding): binding is LayoutBinding {
  return binding.type === "layout";
}

export class LayoutBindingUtil extends BindingUtil<LayoutBinding> {
  static override type = "layout" as const;

  override getDefaultProps() {
    return {
      index: "a1" as IndexKey,
      placeholder: true,
    };
  }

  override onAfterCreate({
    binding,
    updateArrows = true,
  }: BindingOnCreateOptions<LayoutBinding> & { updateArrows?: boolean }): void {
    if (binding.props.placeholder) return;
    this.updateElementsForContainer(binding, undefined, updateArrows);
  }

  override onAfterChange({
    bindingAfter,
  }: BindingOnChangeOptions<LayoutBinding>): void {
    if (bindingAfter.props.placeholder) return;
    this.updateElementsForContainer(bindingAfter);
  }

  override onAfterChangeFromShape({
    binding,
    shapeAfter,
  }: BindingOnShapeChangeOptions<LayoutBinding>): void {
    if (binding.props.placeholder) return;
    const util = this.editor.getShapeUtil(shapeAfter);

    const minW =
      "getMinWidth" in util && typeof util.getMinWidth === "function"
        ? (util.getMinWidth as (shape: TLShape) => number)(shapeAfter)
        : 0;

    const overrideWidth =
      "w" in shapeAfter.props && typeof shapeAfter.props.w === "number"
        ? Math.max(minW, shapeAfter.props.w)
        : undefined;

    this.updateElementsForContainer(binding, overrideWidth);
  }

  override onAfterDelete({
    binding,
  }: BindingOnDeleteOptions<LayoutBinding>): void {
    this.updateElementsForContainer(binding, undefined, true);
  }

  private updateElementsForContainer(
    { props: { placeholder }, fromId: containerId, toId }: LayoutBinding,
    overrideWidth?: number,
    updateArrows?: boolean,
  ) {
    // Get all of the bindings from the layout container
    const container = this.editor.getShape<LogicShape | FeedShape>(containerId);
    if (!container) return;

    const all = this.editor
      .getBindingsFromShape<LayoutBinding>(container, "layout")
      .sort((a, b) => (a.props.index > b.props.index ? 1 : -1));

    // A Feed stacks its Page section (layers) first, then its Rules root.
    // Every other container has one stack.
    const feed = isFeedShape(container);
    const bindings = feed
      ? [
          ...all.filter((b) => {
            const s = this.editor.getShape(b.toId);
            return s && isPageRegionChild(s);
          }),
          ...all.filter((b) => {
            const s = this.editor.getShape(b.toId);
            return s && !isPageRegionChild(s);
          }),
        ]
      : all;
    const pageCount = feed
      ? bindings.filter((b) => {
          const s = this.editor.getShape(b.toId);
          return s && isPageRegionChild(s);
        }).length
      : 0;
    const sectionGap = feed && pageCount > 0 && bindings.length > pageCount ? FEED_SECTION_GAP : 0;

    const gutter = container.props.padding.left + container.props.padding.right;
    const minW = getMinWForLogic(this.editor, container);
    let cumulativeHeight = 0;
    let maxWidth = 0;

    if (!container.props.isCollapsed) {
      for (let i = 0; i < bindings.length; i++) {
        const binding = bindings[i];

        const inPage = feed && pageCount > 0 && i < pageCount;
        const offset = new Vec(
          container.props.padding.left + (inPage ? PAGE_REGION_INSET : 0),
          container.props.padding.top +
            cumulativeHeight +
            i * container.props.padding.gap +
            // The divider between the two sections, once past the last layer.
            (feed && pageCount > 0 && i >= pageCount ? sectionGap : 0),
        );

        if (!binding) {
          continue;
        }

        const toShape = this.editor.getShape<CustomShapes>(binding.toId);

        cumulativeHeight += toShape?.props.h || 0;
        // Only the rules root sizes the Feed. A layer is FITTED to the Feed
        // (below), never the other way round — counting it here let a layer
        // stored at its old full width widen the Feed to suit itself, and the
        // two rules cancelled: it never refitted and hung out of the Page box.
        if (!inPage) maxWidth = Math.max(maxWidth, toShape?.props.w || 0);

        if (toId === binding.toId && placeholder) continue;

        const shape = this.editor.getShape<CustomShapes>(binding.toId);
        if (!shape) continue;

        const point = this.editor.getPointInParentSpace(
          shape,
          this.editor.getShapePageTransform(container).applyToPoint(offset),
        );

        // Layers are ALWAYS fitted to the Page box, not only when the
        // container is being resized: a layer stored before the box was
        // inset kept its full width and hung out of the frame on the right.
        // A layer's floor is its OWN minimum, not the Feed's: the Feed's
        // minimum is the rules root's width, which a layer must sit inside.
        const layerMinW = "minW" in shape.props && typeof shape.props.minW === "number" ? shape.props.minW : 0;
        const fittedW =
          inPage && !binding.props.placeholder
            ? Math.max(layerMinW, (overrideWidth ?? container.props.w) - gutter - 2 * PAGE_REGION_INSET)
            : overrideWidth && !binding.props.placeholder
              ? Math.max(minW, overrideWidth - gutter)
              : undefined;

        if (
          shape.x !== point.x ||
          shape.y !== point.y ||
          (fittedW !== undefined && "w" in shape.props && shape.props.w !== fittedW)
        ) {
          this.editor.updateShape({
            id: binding.toId,
            type: shape.type,
            x: point.x,
            y: point.y,
            props: fittedW !== undefined ? { ...shape.props, w: fittedW } : undefined,
          });

          const util = this.editor.getShapeUtil<BindableUtil>(shape);

          if (updateArrows && util) {
            util.translateAllArrowShapes(
              shape,
              point.x - shape.x,
              point.y - shape.y,
              true,
            );
          }
        }
      }
    }

    const width = Math.max(
      minW,
      placeholder
        ? container.props.w
        : overrideWidth || Math.max(140, maxWidth + gutter),
    );
    const height = container.props.isCollapsed
      ? container.props.h
      : (container.props.isCollapsed ? 0 : container.props.padding.top) +
        (cumulativeHeight +
          Math.max(0, bindings.length - 1) * container.props.padding.gap +
          sectionGap) +
        (container.props.isCollapsed ? 0 : container.props.padding.bottom);

    if (width !== container.props.w || height !== container.props.h) {
      this.editor.updateShape({
        id: container.id,
        type: container.type,
        props: {
          w: Math.max(minW, width),
          h: height,
        },
      });

      const parentShapes = this.editor.getBindingsToShape<LayoutBinding>(
        container,
        "layout",
      );

      for (const parentShape of parentShapes) {
        const util = this.editor.getBindingUtil(parentShape);
        util.onAfterCreate?.({ binding: parentShape });
      }
    }
  }
}

export function allBoundChildren(editor: Editor, shape: TLShape): TLShape[] {
  const queue = editor.getBindingsFromShape<LayoutBinding>(shape, "layout");
  const shapes: TLShape[] = [];

  while (queue.length > 0) {
    const binding = queue.pop();

    if (!binding) {
      continue;
    }

    const child = editor.getShape(binding.toId);

    if (child) {
      shapes.push(child);
    }

    if (
      child &&
      (isLogicShape(child) ||
        isCustomNodeShape(child) ||
        isLayerShape(child) ||
        isAudienceLogicShape(child))
    ) {
      queue.push(
        ...editor.getBindingsFromShape<LayoutBinding>(child, "layout"),
      );
    }
  }

  return shapes;
}
